import React, {
  useMemo,
  Fragment,
  useEffect,
  useState,
  useContext,
} from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { find, pickBy, uniq, orderBy } from 'lodash';
import { featureEnabled, formatISODateStr, getLocalTimeZone } from 'utils';
import { AppointmentListItem, Title, Icon, Tooltip } from 'components';
import { AppointmentController } from 'api';

import { AppointmentWithThumbnail } from '@bb/domain/dist/types';
import { NoShowingsSVG } from '@brokerbay/elements-web';
import { AppointmentPermission } from 'components/List/ListItems/AppointmentItemActions';
import { AuthContext } from 'contexts';
import { VirtualTourWithThumbnail, ESTourItemResponse } from '../types';
import { EmptyContainer, EmptySection } from '../components';
import DashboardTour, { isItemVisible } from '../DashboardTour';
import { getTourItemDateToShow } from '../../TourEditPage/utils';

const renderDate = (date: string) => {
  if (moment(date).startOf('day').isSame(moment().startOf('day'))) {
    return 'Today';
  }
  if (
    moment(date).startOf('day').isSame(moment().add(1, 'day').startOf('day'))
  ) {
    return 'Tomorrow';
  }
  const dateFormat =
    moment().year() !== moment(date).year() ? 'ddd MMM D, YYYY' : 'ddd MMM D';
  return moment(date).format(dateFormat);
};

interface TabSectionProps {
  isBuyer?: boolean;
  appointments: (
    | AppointmentWithThumbnail
    | VirtualTourWithThumbnail
    | ESTourItemResponse
  )[];
  supplementaryTourItems?: ESTourItemResponse[];
  showFullTour?: boolean;
  team?: boolean;
}

const TabSection: React.FC<TabSectionProps> = ({
  isBuyer,
  appointments,
  supplementaryTourItems = [],
  showFullTour,
  team,
}) => {
  const { auth } = useContext(AuthContext);
  const chronologicalShowingsEnabled = featureEnabled('chronologicalShowings');
  const apptToDates = useMemo(() => {
    const allAppointments = [
      ...appointments,
      ...supplementaryTourItems.map(appt => ({
        ...appt,
        isSupplementary: true,
      })),
    ];

    // make sure all the items of the tour are in the same date.
    // might not be necessary, just in case.
    const toursDateMap = new Map<string, string>();
    // After timezone refactor, legacy date field is not used
    const appointmentWithDate = allAppointments.map(appointment => {
      let date;
      if ('appointmentTourId' in appointment) {
        if (toursDateMap.get(appointment.appointmentTourId)) {
          date = toursDateMap.get(appointment.appointmentTourId);
        } else {
          date = getTourItemDateToShow(
            appointment as ESTourItemResponse,
            getLocalTimeZone(auth),
          );
          toursDateMap.set(appointment.appointmentTourId, date);
        }
      } else {
        date = formatISODateStr(appointment.time, getLocalTimeZone(auth));
      }
      return {
        ...appointment,
        date,
      };
    });
    const sortedDate = chronologicalShowingsEnabled
      ? uniq(appointmentWithDate.map(({ date }) => date)).sort()
      : uniq(appointmentWithDate.map(({ date }) => date))
          .sort()
          .reverse();

    const grouped: {
      date: string;
      sortedAppts: (AppointmentWithThumbnail | VirtualTourWithThumbnail)[];
      tours: Record<
        string, // tourId
        Record<
          string | 'firstItem', // AppointmentTourItemId | 'firstItem'
          (ESTourItemResponse | AppointmentWithThumbnail) & {
            isSupplementary?: boolean;
          }
        >
      >;
    }[] = [];
    const dateMap: Record<
      string,
      (AppointmentWithThumbnail | VirtualTourWithThumbnail)[]
    > = {};
    const dateMapForTours: Record<
      string, // date of the tour
      Record<
        string, // tourId
        Record<
          string | 'firstItem', // AppointmentTourItemId | 'firstItem'
          (ESTourItemResponse | AppointmentWithThumbnail) & {
            isSupplementary?: boolean;
          }
        >
      >
    > = {};
    sortedDate.forEach(date => {
      dateMap[date] = [];
      dateMapForTours[date] = {};
    });

    // This step of extra sorting is needed for sorting within same date
    const sortedAppts = orderBy(
      appointmentWithDate,
      ({ time }) => new Date(time!).getTime(),
      chronologicalShowingsEnabled ? 'asc' : 'desc',
    );

    sortedAppts.forEach(appt => {
      if (
        isBuyer &&
        (appt.docType === 'tourItem' || appt?.appointmentTourId) &&
        typeof appt?.tourName === 'string'
      ) {
        dateMapForTours[appt.date][appt.appointmentTourId] =
          dateMapForTours[appt.date][appt.appointmentTourId] || {};
        if (appt.isFirstItem) {
          dateMapForTours[appt.date][appt.appointmentTourId].firstItem =
            appt as ESTourItemResponse | AppointmentWithThumbnail;
        } else {
          dateMapForTours[appt.date][appt.appointmentTourId][
            (appt as AppointmentWithThumbnail).appointmentTourItemId || appt.id
          ] = appt as ESTourItemResponse & { isSupplementary: boolean };
        }
      } else if (!appt.appointmentTourId || !isBuyer) {
        dateMap[appt.date].push(
          appt as AppointmentWithThumbnail | VirtualTourWithThumbnail,
        );
      }
    });

    sortedDate.forEach(date => {
      // skip tours that have nothing to show.
      const toursToShow = pickBy(dateMapForTours[date], tour =>
        find(tour, item => isItemVisible(item, showFullTour)),
      );
      // skip dates that have no appt nor tours
      if (Object.keys(toursToShow).length || dateMap[date].length) {
        grouped.push({
          date,
          sortedAppts: dateMap[date],
          tours: toursToShow,
        });
      }
    });
    return grouped;
  }, [
    appointments,
    isBuyer,
    showFullTour,
    supplementaryTourItems,
    chronologicalShowingsEnabled,
  ]);

  const [listingAppointmentPermissions, setListingAppointmentPermissions] =
    useState<{ [i: string]: AppointmentPermission }>({});
  useEffect(() => {
    if (isBuyer) return;

    const getPermissions = async () => {
      // getting appointments ids for permissions and filtering out appointments that don't have id (virtual tours)
      const allAppointments = appointments
        .map(appointment => appointment.id)
        .filter(
          appointment => appointment !== null && appointment !== undefined,
        );
      if (allAppointments.length === 0) return;
      const data =
        await AppointmentController.getPermissionsManyAppointments(
          allAppointments,
        );

      setListingAppointmentPermissions({ ...data });
    };
    getPermissions();
  }, [appointments, isBuyer]);

  if (!apptToDates.length) {
    if (featureEnabled('showingsMadeEasyFeature') && !team) {
      return (
        <EmptySection
          dataCy="appointment-card-no-showings"
          Image={<NoShowingsSVG width={156} height={120} />}
          mainText="No showings yet"
          secondaryText={
            isBuyer
              ? 'Find a listing of interest to book a showing.'
              : 'View booked showings for your listings here.'
          }
        />
      );
    }
    // Team dashboard will continue to use old empty design for now
    return (
      <EmptyContainer data-cy="appointment-card-no-showings">
        <Icon type="calendar" width={32} color="gray6" />
        <span>No showings yet</span>
      </EmptyContainer>
    );
  }

  return (
    <TabSectionContainer>
      {apptToDates.map(({ sortedAppts, date, tours }, index) => (
        <Fragment key={`appts-date-${date}`}>
          <DateHeader>
            <DateTitle data-cy="appointment-card-date-header" level={3}>
              {renderDate(date)}
            </DateTitle>
            {isBuyer && (
              <Tooltip title="Print Showings">
                <a
                  href={`/get_appointments_instructions#?date=${date}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Icon type="antdPrinterOutlined" />
                </a>
              </Tooltip>
            )}
          </DateHeader>

          {/* appointments that are not in a tour */}
          {sortedAppts.map((appointment, apptIndex) => (
            <AppointmentListItem
              key={`appt-item-${apptIndex}`}
              appointment={appointment}
              isBuyer={Boolean(isBuyer)}
              isLastItem={
                index === apptToDates.length - 1 &&
                apptIndex === sortedAppts.length - 1
              }
              listingAppointmentPermissions={listingAppointmentPermissions}
              team={team}
            />
          ))}

          {/* Tours */}
          {isBuyer &&
            Object.keys(tours)?.map(tourId => (
              <DashboardTour
                tour={tours[tourId]}
                key={tourId}
                showFullTour={showFullTour}
              />
            ))}
        </Fragment>
      ))}
    </TabSectionContainer>
  );
};

export default TabSection;

const TabSectionContainer = styled.div`
  padding: 16px 14px 5px 14px;
`;

const DateHeader = styled.div`
  margin-bottom: 10px;
`;

const DateTitle = styled(Title)`
  display: inline;
  margin-right: 12px;
`;
